/**
 * GSAP & ScrollTrigger Advanced Motion Engine
 * High-performance scroll-driven animations inspired by Datahex
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export function initGSAPAnimations() {
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) return;

    // 1. Hero Entrance Timeline
    const heroTl = gsap.timeline({ defaults: { ease: 'power3.out', duration: 1.1 } });
    heroTl.from('.hero-heading .eyebrow', { opacity: 0, y: 20, delay: 0.1 })
          .from('.hero-heading h1', { opacity: 0, y: 35, duration: 1.2 }, '-=0.8')
          .from('.floating-tag', { opacity: 0, y: 15, stagger: 0.1, duration: 0.8 }, '-=0.9')
          .from('.hero-description', { opacity: 0, y: 25 }, '-=0.8')
          .from('.hero-main-image', { opacity: 0, y: 40, scale: 0.96, duration: 1.3 }, '-=0.9')
          .from('.hero-photo-pair figure', { opacity: 0, y: 30, stagger: 0.2 }, '-=1.0');

    // 2. ScrollTrigger Parallax on Hero Images
    gsap.to('.hero-main-image img', {
        yPercent: -12,
        ease: 'none',
        scrollTrigger: {
            trigger: '.hero-gallery',
            start: 'top bottom',
            end: 'bottom top',
            scrub: 1.2
        }
    });

    gsap.to('.hero-photo-pair figure:first-child img', {
        yPercent: -16,
        ease: 'none',
        scrollTrigger: {
            trigger: '.hero-gallery',
            start: 'top bottom',
            end: 'bottom top',
            scrub: 1.5
        }
    });

    gsap.to('.hero-photo-pair figure:last-child img', {
        yPercent: 12,
        ease: 'none',
        scrollTrigger: {
            trigger: '.hero-gallery',
            start: 'top bottom',
            end: 'bottom top',
            scrub: 1.5
        }
    });

    // 3. Staggered Service Stories Reveal on Scroll
    gsap.utils.toArray('.service-stories article').forEach((card, idx) => {
        gsap.from(card, {
            opacity: 0,
            y: 50,
            duration: 0.9,
            ease: 'power3.out',
            scrollTrigger: {
                trigger: card,
                start: 'top 85%',
                toggleActions: 'play none none reverse'
            }
        });
    });

    // 4. Core Services Grid Staggered Reveal with Glow
    gsap.from('.services .service', {
        opacity: 0,
        y: 45,
        stagger: 0.12,
        duration: 0.85,
        ease: 'power3.out',
        scrollTrigger: {
            trigger: '.services',
            start: 'top 82%'
        }
    });

    // 5. From Setup to Scale 4-Stage Roadmap Stagger
    gsap.from('.page-section .ai-beam-card', {
        opacity: 0,
        y: 40,
        scale: 0.96,
        stagger: 0.14,
        duration: 0.8,
        ease: 'back.out(1.4)',
        scrollTrigger: {
            trigger: '.page-section',
            start: 'top 80%'
        }
    });

    // 6. Ecosystem Interactive Hub Pulse on Scroll
    const ecoTl = gsap.timeline({
        scrollTrigger: {
            trigger: '#ecosystem',
            start: 'top 75%'
        }
    });

    ecoTl.from('.ecosystem-layout > div:first-child', { opacity: 0, x: -35, duration: 0.8, ease: 'power2.out' })
         .from('.ecosystem-map', { opacity: 0, scale: 0.92, duration: 0.9, ease: 'power3.out' }, '-=0.5')
         .from('.system-row span', { opacity: 0, y: 15, stagger: 0.06, duration: 0.5, ease: 'back.out(1.5)' }, '-=0.5')
         .from('.system-hub', { opacity: 0, scale: 0.75, duration: 0.7, ease: 'elastic.out(1, 0.6)' }, '-=0.4');

    // 7. Process Steps Cascading Timeline
    gsap.from('.process article', {
        opacity: 0,
        y: 35,
        stagger: 0.15,
        duration: 0.75,
        ease: 'power2.out',
        scrollTrigger: {
            trigger: '.process',
            start: 'top 82%'
        }
    });

    // 8. Industries Gallery Parallax & Zoom on Scroll
    gsap.utils.toArray('.industry-card').forEach((card, i) => {
        gsap.from(card, {
            opacity: 0,
            y: 40,
            duration: 0.8,
            delay: i * 0.1,
            ease: 'power3.out',
            scrollTrigger: {
                trigger: card,
                start: 'top 88%'
            }
        });

        const img = card.querySelector('img');
        if (img) {
            gsap.to(img, {
                yPercent: -10,
                ease: 'none',
                scrollTrigger: {
                    trigger: card,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: 1.2
                }
            });
        }
    });

    // 9. Metric Numbers Smooth Scrub / Counter Animation
    const counterElements = document.querySelectorAll('[data-counter]');
    counterElements.forEach(el => {
        const targetVal = parseFloat(el.getAttribute('data-counter') || el.innerText.replace(/[^0-9.]/g, ''));
        const prefix = el.getAttribute('data-prefix') || '';
        const suffix = el.getAttribute('data-suffix') || '';
        const isDecimal = String(targetVal).includes('.');
        const obj = { val: 0 };

        ScrollTrigger.create({
            trigger: el,
            start: 'top 85%',
            once: true,
            onEnter: () => {
                gsap.to(obj, {
                    val: targetVal,
                    duration: 1.8,
                    ease: 'power2.out',
                    onUpdate: () => {
                        el.textContent = prefix + (isDecimal ? obj.val.toFixed(1) : Math.floor(obj.val)) + suffix;
                    }
                });
            }
        });
    });

    // 10. Word-by-Word Scroll Text Illuminator (Datahex Scrub Effect)
    const textHeaders = document.querySelectorAll('.scroll-reveal-text');
    textHeaders.forEach(header => {
        if (!header.dataset.gsapSplit) {
            header.dataset.gsapSplit = 'true';
            const words = header.innerText.trim().split(/\s+/);
            header.innerHTML = words.map(w => `<span class="gsap-word" style="display:inline-block; opacity:0.28; transition:color 0.3s ease;">${w}</span> `).join('');
        }

        const words = header.querySelectorAll('.gsap-word');
        gsap.to(words, {
            opacity: 1,
            color: '#081B33',
            stagger: 0.08,
            ease: 'power1.inOut',
            scrollTrigger: {
                trigger: header,
                start: 'top 80%',
                end: 'bottom 45%',
                scrub: 0.8
            }
        });
    });
}
